import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DASHBOARD_URL, DISCORD_URL, GITHUB_URL } from '../config/const.js';
import SpriteAnimatedLogo, { SPRITES } from '../ui/SpriteAnimatedLogo.js';

const Header = () => {
  const [isGooey, setIsGooey] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Only enable gooey effect on desktop (width > 768px)
      const isDesktop = window.innerWidth > 768;

      if (isDesktop) {
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          // Scrolling down
          setIsGooey(true);
        } else if (currentScrollY < lastScrollY) {
          // Scrolling up
          setIsGooey(false);
        }
      } else {
        // Always disable gooey on mobile
        setIsGooey(false);
      }

      setLastScrollY(currentScrollY);
    };

    const handleResize = () => {
      // Disable gooey on mobile when window is resized
      const isDesktop = window.innerWidth > 768;
      if (!isDesktop) {
        setIsGooey(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [lastScrollY]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu on route change
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className={`header ${isGooey ? 'gooey-nav' : ''}`}>
        <div className="header-container">
          <div className="logo">
            <Link to="/">
              <SpriteAnimatedLogo
                width={100}
                height={100}
                frameRate={60}
                autoPlay={false}
                filename={SPRITES.MEMU_LOGO}
                className="logo-image"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="nav desktop-nav">
            <Link to="/" className="nav-item">
              <span>Home</span>
            </Link>
            <Link to="/pricing" className={`nav-item ${location.pathname === '/pricing' ? 'active' : ''}`}>
              Pricing
            </Link>
            <Link to="/blog" className={`nav-item ${location.pathname === '/blog' ? 'active' : ''}`}>
              Blog
            </Link>
            <div className="nav-divider"></div>
            <Link to="/docs" className={`nav-item ${location.pathname === '/docs' ? 'active' : ''}`}>
              <span>Docs</span>
              <i className="fa-solid fa-arrow-up-right-from-square"></i>
            </Link>
          </nav>

          <div className="header-right">
            <div className="social-links desktop-only">
              <a href={GITHUB_URL} className="github-link" target="_blank" rel="noopener noreferrer">
                <span className="github-icon"><i className="fab fa-github"></i></span>
              </a>
              <a href={DISCORD_URL} className="discord-link" target="_blank" rel="noopener noreferrer">
                <span className="discord-icon"><i className="fab fa-discord"></i></span>
              </a>
            </div>
            <a href={DASHBOARD_URL} className="btn btn-primary btn-sm desktop-only" target="_blank" rel="noopener noreferrer">
              Get started
            </a>

            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-btn"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={closeMobileMenu}>
        <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
          <div className="mobile-menu-header">
            <div className="mobile-logo">
              <Link to="/" onClick={closeMobileMenu}>
                <SpriteAnimatedLogo
                  width={80}
                  height={80}
                  frameRate={60}
                  autoPlay={false}
                  filename={SPRITES.MEMU_LOGO}
                  className="logo-image"
                />
              </Link>
            </div>
            <button className="mobile-menu-close" onClick={closeMobileMenu}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <nav className="mobile-nav">
            <a href="#usecases" className="mobile-nav-item" onClick={closeMobileMenu}>
              <span>Usecases</span>
              <i className="fas fa-chevron-right"></i>
            </a>
            <Link
              to="/pricing"
              className={`mobile-nav-item ${location.pathname === '/pricing' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              <span>Pricing</span>
              <i className="fas fa-chevron-right"></i>
            </Link>
            <Link
              to="/blog"
              className={`mobile-nav-item ${location.pathname === '/blog' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              <span>Blog</span>
              <i className="fas fa-chevron-right"></i>
            </Link>
            <Link
              to="/docs"
              className={`mobile-nav-item ${location.pathname === '/docs' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              <span>Docs</span>
              <i className="fas fa-external-link-alt"></i>
            </Link>
          </nav>

          <div className="mobile-menu-footer">
            <div className="mobile-social-links">
              <a href={GITHUB_URL} className="mobile-github-link" target="_blank" rel="noopener noreferrer">
                <span className="github-icon"><i className="fab fa-github"></i></span>
              </a>
              <a href={DISCORD_URL} className="mobile-discord-link" target="_blank" rel="noopener noreferrer">
                <span className="discord-icon"><i className="fab fa-discord"></i></span>
              </a>
            </div>
            <button className="btn btn-primary btn-lg mobile-cta" onClick={closeMobileMenu}>
              Get started
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;